import { Mesh, TextureLoader, NoColorSpace, LinearFilter, ClampToEdgeWrapping, DoubleSide, Color } from 'three/webgpu'
import { MSDFTextGeometry, MSDFTextNodeMaterial } from 'three-msdf-text-utils/webgpu'
import DomTracked from './DomTracked'
import staticPath from '../utils/staticPath'

const atlases = new Map()

export default class MsdfText extends DomTracked {
	static defaults = { color: null }
	static fonts = { 400: 'Restart-Regular', 500: 'Restart-Medium' }

	static async fromElement(element, options = {}) {
		const { font, map } = await MsdfText.atlas(MsdfText.weightOf(element))

		return new MsdfText(font, map, { element, ...options })
	}

	static weightOf(element) {
		const weight = parseInt(getComputedStyle(element).fontWeight, 10) || 400

		return Object.keys(MsdfText.fonts)
			.map(Number)
			.reduce((best, current) => (Math.abs(current - weight) < Math.abs(best - weight) ? current : best))
	}

	static atlas(weight) {
		if (!atlases.has(weight)) {
			const base = staticPath(`fonts/msdf/${MsdfText.fonts[weight]}`)

			atlases.set(weight, MsdfText.load(`${base}.json`, `${base}.png`))
		}

		return atlases.get(weight)
	}

	static async load(atlasUrl, textureUrl) {
		const [font, map] = await Promise.all([
			fetch(atlasUrl).then(response => response.json()),
			new TextureLoader().loadAsync(textureUrl),
		])

		map.colorSpace = NoColorSpace
		map.minFilter = LinearFilter
		map.magFilter = LinearFilter
		map.wrapS = ClampToEdgeWrapping
		map.wrapT = ClampToEdgeWrapping
		map.generateMipmaps = false

		return { font, map }
	}

	constructor(font, map, options = {}) {
		super(options)

		this.font = font
		this.map = map

		this.#build()
	}

	#build() {
		const style = getComputedStyle(this.element)
		const rect = this.element.getBoundingClientRect()

		this.fontSize = parseFloat(style.fontSize)

		const width = this.#toAtlas(rect.width)
		const height = this.#toAtlas(rect.height)
		const lineHeight = this.#toAtlas(this.#lineHeight(style))

		this.geometry = new MSDFTextGeometry({
			text: this.element.textContent.trim().replace(/\s+/g, ' '),
			font: this.font,
			align: this.#align(style.textAlign),
			width,
			lineHeight,
			letterSpacing: this.#toAtlas(this.#letterSpacing(style)),
		})

		this.tone = new Color(this.options.color ?? style.color)

		// The MSDF shader already outputs an analytic, sub-pixel alpha. Blend it
		// directly; alphaToCoverage would quantise it to the MSAA sample count.
		this.material = new MSDFTextNodeMaterial({
			map: this.map,
			color: this.tone.clone(),
		})
		this.material.side = DoubleSide
		this.material.depthWrite = false

		const baseline = (lineHeight - this.font.common.lineHeight) / 2 + this.font.common.base
		const firstLineTop = (this.geometry.layout.linesTotal - 1) * lineHeight + baseline

		this.mesh = new Mesh(this.geometry, this.material)
		this.mesh.scale.y = -1
		this.mesh.position.set(-width / 2, height / 2 - firstLineTop, 0)

		this.add(this.mesh)
	}

	set color(value) {
		this.material?.color.value.set(value)
	}

	#toAtlas(pixels) {
		return (pixels / this.fontSize) * this.font.info.size
	}

	#align(value) {
		if (value === 'right' || value === 'end') return 'right'
		if (value === 'center') return 'center'

		return 'left'
	}

	#lineHeight(style) {
		const value = parseFloat(style.lineHeight)

		if (Number.isNaN(value)) return this.fontSize * 1.2

		return value < 4 ? value * this.fontSize : value
	}

	#letterSpacing(style) {
		const value = parseFloat(style.letterSpacing)

		return Number.isNaN(value) ? 0 : value
	}

	layout() {
		this.scale.setScalar((this.worldPerPixel * this.fontSize) / this.font.info.size)
	}

	onResize() {
		this.remove(this.mesh)
		this.geometry.dispose()
		this.material.dispose()
		this.#build()
	}

	dispose() {
		this.geometry.dispose()
		this.material.dispose()
		super.dispose()
	}
}
